import React, { useMemo } from 'react';

import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import OverrideControls from './OverrideControls';
import ProductFeedbackSettings from './ProductFeedbackSettings';
import RecordingSettings from './RecordingSettings';
import { ClassLevelOverrideOptions } from '../../../types/adminConfig';
import AdminAuthContainer from '../AdminAuthContainer';
import AdminSettingsContainer from '../AdminSettingsContainer';
import SaveButton from '../components/SaveButton';
import { H2, StyledFormGroupContainer } from '../styles/headings';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';
import { showNonCollabSetting } from '../utils/showAdvancedSetting';

const FeaturesSettings = () => {
    const { school, loadingSchool } = useContainer(AdminSettingsContainer);
    const {
        adminSettings,
        setAdminSettings,
        showExperimentalSettings,
        isSavingSettingsData,
        handleSaveSettings,
        isSavingDisabled
    } = useContainer(AdminSettingsContainer);
    const { isAdmin, isSuperAdmin } = useContainer(AdminAuthContainer);
    const { featureSettings, authSettings } = adminSettings;
    const { t } = useTranslation('admin_settings');

    const nonCollabSettingShown = useMemo(
        () => showNonCollabSetting({ school, loadingSchool, isSuperAdmin }),
        [school, loadingSchool, isSuperAdmin]
    );

    const isTeams = school?.isTeams;

    /**
     * Legacy features (one per column)
     */
    const handleFeatureSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    /**
     * Current features (in options column)
     */
    const handleUpdateOptionsCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings.options[event.target.name] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const handleAuthSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOverrides = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings.overrides[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    return (
        <>
            <H2>{t('configuration.title')}</H2>
            <StyledFormGroupContainer>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.classAssessments}
                                onChange={handleFeatureSettingsChange}
                                id="classAssessments"
                                name="classAssessments"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.assessments') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.assignments}
                                onChange={handleFeatureSettingsChange}
                                id="assignments"
                                name="assignments"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.assignments') as string}
                    />
                    {nonCollabSettingShown && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    checked={featureSettings.chat}
                                    onChange={handleFeatureSettingsChange}
                                    id="chat"
                                    name="chat"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.chat') as string}
                        />
                    )}
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.classGradebook}
                                onChange={handleFeatureSettingsChange}
                                id="classGradebook"
                                name="classGradebook"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.gradebook') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.hasShareFiles}
                                onChange={handleFeatureSettingsChange}
                                id="hasShareFiles"
                                name="hasShareFiles"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.share-files') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.lms}
                                onChange={handleFeatureSettingsChange}
                                id="lms"
                                name="lms"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.lms') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.loseFocusTracking}
                                onChange={handleFeatureSettingsChange}
                                id="loseFocusTracking"
                                name="loseFocusTracking"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.loseFocusTracking') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.surveys}
                                onChange={handleFeatureSettingsChange}
                                id="surveys"
                                name="surveys"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.surveys') as string}
                    />
                    {nonCollabSettingShown && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    checked={featureSettings.polling}
                                    onChange={handleFeatureSettingsChange}
                                    id="polling"
                                    name="polling"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.polling') as string}
                        />
                    )}
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.proctoring}
                                onChange={handleFeatureSettingsChange}
                                id="proctoring"
                                name="proctoring"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.proctoring') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.classDocument}
                                onChange={handleFeatureSettingsChange}
                                id="classDocument"
                                name="classDocument"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.syllabus') as string}
                    />
                    {nonCollabSettingShown && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.sendInviteEmails}
                                    onChange={handleFeatureSettingsChange}
                                    id="sendInviteEmails"
                                    name="sendInviteEmails"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.inviteEmails') as string}
                        />
                    )}
                    {nonCollabSettingShown && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.whiteboard}
                                    onChange={handleFeatureSettingsChange}
                                    id="whiteboard"
                                    name="whiteboard"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.whiteboard') as string}
                        />
                    )}
                    {nonCollabSettingShown && !isTeams && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={authSettings.allowPMI}
                                    onChange={handleAuthSettingsChange}
                                    id="allowPMI"
                                    name="allowPMI"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('auth.allowPMI') as string}
                        />
                    )}

                    {!isTeams && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.google}
                                    onChange={handleFeatureSettingsChange}
                                    id="google"
                                    name="google"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.googleDrive.text') as string}
                        />
                    )}
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.msOffice}
                                onChange={handleFeatureSettingsChange}
                                id="msOffice"
                                name="msOffice"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.microsoftOneDrive') as string}
                    />

                    {!isTeams && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.enableBreakoutRoomSecurity}
                                    onChange={handleFeatureSettingsChange}
                                    id="enableBreakoutRoomSecurity"
                                    name="enableBreakoutRoomSecurity"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.enable-breakout-room-security-v2') as string}
                        />
                    )}

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.allowTabLocking}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="allowTabLocking"
                                name="allowTabLocking"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.allow-tab-locking') as string}
                        hint={isTeams ? undefined : t('configuration.allow-tab-locking-tooltip')}
                    />

                    {showExperimentalSettings && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.autoTranscriptions}
                                    onChange={handleFeatureSettingsChange}
                                    id="autoTranscriptions"
                                    name="autoTranscriptions"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.autoTranscriptions') as string}
                        />
                    )}
                </FormGroup>
            </StyledFormGroupContainer>

            <FormGroup>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={featureSettings.options.hasPresentations}
                            onChange={handleUpdateOptionsCheckboxChange}
                            id="hasPresentations"
                            name="hasPresentations"
                        />
                    }
                    label={t('configuration.allow-presentations')}
                />
                <OverrideControls
                    overrideName="hasPresentations"
                    overrideValue={featureSettings.overrides?.hasPresentations ?? ClassLevelOverrideOptions.NoOne}
                    handleUpdateOverrides={handleUpdateOverrides}
                    labelText={t('whoCanOverride')}
                    tooltipText={t('whoCanOverrideTooltip')}
                />
            </FormGroup>

            {!isTeams && (
                <>
                    <ProductFeedbackSettings />
                </>
            )}

            <RecordingSettings />

            <SaveButton isSaving={isSavingSettingsData} disabled={isSavingDisabled} onClick={handleSaveSettings} />
        </>
    );
};

export default FeaturesSettings;
